import React from 'react';
import type { ProcessListItemDto } from '@api/processes';
import ProcessActionsMenu from './ProcessActionsMenu';

export interface ProcessRowActionsProps {
  process: ProcessListItemDto;
  onRename: (process: ProcessListItemDto) => void;
  onDuplicate: (process: ProcessListItemDto) => void;
  onSendCopy: (process: ProcessListItemDto) => void;
  onDelete: (process: ProcessListItemDto) => void;
  /** Suppress the menu while a global mutation is in flight. */
  disabled?: boolean;
  dataTestId?: string;
}

/**
 * Per-row kebab menu in the Processes list. Stops propagation on the
 * trigger so opening the menu doesn't also fire the row's `onClick`
 * (which navigates to the process). Mirrors the prototype's row menu —
 * Open is intentionally absent because the row click + visible "Open"
 * button already cover that path.
 *
 * This is a thin DTO-aware adapter over `ProcessActionsMenu`: it
 * computes `canRename` / `canDelete` from the row's permissions
 * (`isOwner`, `isEditable`, `status`) and curries each callback with
 * the row's DTO. The visual menu (kebab + ChipMenu shell, item set,
 * danger tone for Delete, tooltips, alignment) lives in
 * `ProcessActionsMenu` and is reused by the editor's panel kebab.
 */
const ProcessRowActions: React.FC<ProcessRowActionsProps> = ({
  process,
  onRename,
  onDuplicate,
  onSendCopy,
  onDelete,
  disabled = false,
  dataTestId = 'process-row-actions',
}) => {
  const canRename = process.isOwner;

  const canDelete = process.isOwner;
  const deleteTooltip: string | undefined = process.isOwner
    ? undefined
    : 'Only the owner can delete this process';

  return (
    <ProcessActionsMenu
      ariaLabel={`Actions for ${process.name}`}
      disabled={disabled}
      onRename={canRename ? () => onRename(process) : undefined}
      onDuplicate={() => onDuplicate(process)}
      onSendCopy={() => onSendCopy(process)}
      onDelete={() => onDelete(process)}
      deleteDisabled={!canDelete}
      deleteTooltip={deleteTooltip}
      dataTestId={dataTestId}
    />
  );
};

export default ProcessRowActions;
